/**
 * BillPaymentHistory — list of payments[] entries for a single purchase bill
 * with per-row edit + delete. iter181 extracted to its own module (iter200)
 * so the modals file can import it without circular reference back to Purchases.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Pencil, Trash2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function BillPaymentHistory({ billId, onChanged }) {
  const [data, setData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ amount: '', date: '', method: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/purchases/bills/${billId}/payments`, { withCredentials: true });
      setData(r.data);
    } catch {
      // 404 or unsupported — silently empty
      setData({ payments: [] });
    }
  }, [billId]);

  useEffect(() => { load(); }, [load]);

  if (!data || !(data.payments || []).length) {
    return null;  // Hide section when bill has no individual payment rows
  }

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      amount: String(p.amount || ''),
      date:   (p.date || '').slice(0, 10),
      method: p.method || '',
      note:   p.note || '',
    });
  };

  const saveEdit = async (paymentId) => {
    if (!editForm.amount || parseFloat(editForm.amount) <= 0) {
      toast.error('Amount must be > 0'); return;
    }
    setBusy(true);
    try {
      await axios.put(
        `${API}/purchases/bills/${billId}/payments/${paymentId}`,
        { amount: parseFloat(editForm.amount), date: editForm.date, method: editForm.method, note: editForm.note },
        { withCredentials: true },
      );
      toast.success('Payment updated · bill recomputed');
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const deletePayment = async (paymentId, amount) => {
    if (!window.confirm(`Delete this payment of ${fmt(amount)}? Bill totals will recompute.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/purchases/bills/${billId}/payments/${paymentId}`, { withCredentials: true });
      toast.success('Payment removed');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-3" data-testid="bill-payment-history">
      <div className="text-xs text-zinc-300 font-semibold mb-2 flex items-center gap-1.5">
        <CreditCard size={12} className="text-cyan-400"/>
        Payment History ({(data.payments || []).length})
      </div>
      <div className="space-y-1.5">
        {(data.payments || []).slice().reverse().map(p => (
          <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-xs" data-testid={`payment-row-${p.id}`}>
            {editingId === p.id ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="any" value={editForm.amount}
                    onChange={e => setEditForm(f => ({ ...f, amount: e.target.value }))}
                    data-testid={`edit-pay-amount-${p.id}`}
                    placeholder="Amount"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-amber-400 font-mono"/>
                  <input type="date" value={editForm.date}
                    onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200"/>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={editForm.method}
                    onChange={e => setEditForm(f => ({ ...f, method: e.target.value }))}
                    placeholder="Method"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200"/>
                  <input value={editForm.note}
                    onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))}
                    placeholder="Note"
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-zinc-200"/>
                </div>
                <div className="flex justify-end gap-1.5">
                  <button onClick={() => setEditingId(null)}
                    className="px-2 py-1 rounded text-zinc-400 hover:text-zinc-200 text-[11px]">Cancel</button>
                  <button onClick={() => saveEdit(p.id)} disabled={busy}
                    data-testid={`save-pay-${p.id}`}
                    className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold px-3 py-1 rounded text-[11px] inline-flex items-center gap-1">
                    <Check size={10}/> Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-green-400 font-semibold">{fmt(p.amount)}</span>
                    <span className="text-zinc-500">{(p.date || '').slice(0, 10)}</span>
                    {p.method && <span className="bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded text-[10px]">{p.method}</span>}
                    {p.bank_statement_line_id && <span className="bg-cyan-950 text-cyan-300 px-1.5 py-0.5 rounded text-[10px]">Bank Reco</span>}
                  </div>
                  {p.note && <div className="text-zinc-500 text-[10px] mt-0.5 truncate">{p.note}</div>}
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => startEdit(p)} data-testid={`edit-pay-${p.id}`}
                    className="p-1 rounded bg-zinc-800 hover:bg-amber-900/40 text-zinc-400 hover:text-amber-300">
                    <Pencil size={10}/>
                  </button>
                  <button onClick={() => deletePayment(p.id, p.amount)} data-testid={`del-pay-${p.id}`}
                    className="p-1 rounded bg-red-900/30 hover:bg-red-900/50 text-red-400">
                    <Trash2 size={10}/>
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
